import { useCallback, useState } from "react";
import type { ParsedOrderResult, PaymentMethod, Product } from "@ai-pos/shared";
import { orderParserApi } from "../../api/resources.js";
import { useVoiceRecognition } from "../../hooks/useVoiceRecognition.js";
import { useCartStore } from "../../stores/cartStore.js";

interface Props {
  products: Product[];
  paymentMethods: PaymentMethod[];
  onCancel: () => void;
  onApply: () => void;
}

export function AiVoiceConfirmPanel({ products, paymentMethods, onCancel, onApply }: Props) {
  const [text, setText] = useState("");
  const [result, setResult] = useState<ParsedOrderResult | null>(null);
  const [parsing, setParsing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const cart = useCartStore();

  const handleVoiceResult = useCallback((finalText: string) => {
    setText((prev) => (prev ? `${prev}${finalText}` : finalText));
  }, []);

  const voice = useVoiceRecognition(handleVoiceResult);

  const handleParse = async () => {
    if (!text.trim()) return;
    setParsing(true);
    setError(null);
    setResult(null);
    try {
      const res = await orderParserApi.parse(text);
      setResult(res.result);
    } catch (err) {
      setError(err instanceof Error ? err.message : "AI解析に失敗しました。通常の商品ボタン入力をご利用ください。");
    } finally {
      setParsing(false);
    }
  };

  const handleApply = () => {
    if (!result || result.unmatchedItems.length > 0) return;

    for (const item of result.items) {
      const product = products.find((p) => p.name === item.matchedProductName);
      if (!product) continue;
      for (let i = 0; i < item.quantity; i += 1) {
        cart.addItem({
          id: product.id,
          name: product.name,
          priceIncludingTax: product.priceIncludingTax,
          taxRatePercent: product.taxRatePercent,
        });
      }
    }
    if (result.discount > 0) cart.setDiscount(cart.discount + result.discount);
    if (result.guestCount) cart.setGuestCount(result.guestCount);
    if (result.paymentMethodCode) {
      const method = paymentMethods.find((m) => m.code === result.paymentMethodCode);
      if (method) cart.setPaymentMethodId(method.id);
    }
    onApply();
  };

  return (
    <div className="card stack" style={{ maxWidth: 720, margin: "0 auto" }}>
      <h2>音声・テキスト注文入力</h2>
      <p style={{ color: "var(--color-text-muted)" }}>
        店内の騒音を考慮し、短い発話単位（1文ずつ）で話してください。認識結果は確定前に修正できます。
      </p>

      <div className="row">
        <button
          className="btn btn-primary"
          onClick={() => (voice.listening ? voice.stop() : voice.start())}
          disabled={!voice.supported}
        >
          {voice.listening ? "録音停止" : "音声入力"}
        </button>
        {!voice.supported && <span className="badge badge-danger">この端末では音声入力は利用できません</span>}
      </div>

      {voice.interimText && <div className="alert alert-warning">認識中: {voice.interimText}</div>}
      {voice.error && <div className="alert alert-error">{voice.error}</div>}

      <div className="form-field">
        <label htmlFor="order-text">注文テキスト（音声入力結果も反映されます。手動修正可）</label>
        <textarea
          id="order-text"
          rows={4}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="例: 和牛重を二つ、抹茶ラテのアイスを一つ。カード払い"
        />
      </div>

      <button className="btn btn-primary" onClick={() => void handleParse()} disabled={parsing || !text.trim()}>
        {parsing ? "解析中..." : "解析する"}
      </button>

      {error && <div className="alert alert-error">{error}</div>}

      {result && (
        <div className="card">
          <h3>解析結果（確認・修正してください）</h3>
          {result.items.length === 0 && result.unmatchedItems.length === 0 && (
            <p className="empty-state">商品を認識できませんでした。テキストを修正して再度解析してください。</p>
          )}
          {result.items.map((item, index) => (
            <div key={`${item.matchedProductName}-${index}`} className="row">
              <span className="badge">一致</span>
              <span>
                {item.matchedProductName} × {item.quantity}
              </span>
            </div>
          ))}
          {result.unmatchedItems.map((name, index) => (
            <div key={`${name}-${index}`} className="row">
              <span className="badge badge-danger">未一致</span>
              <span>{name}</span>
            </div>
          ))}
          {result.unmatchedItems.length > 0 && (
            <div className="alert alert-warning" style={{ marginTop: 8 }}>
              商品マスタに存在しない商品が含まれているため、カートに反映できません。テキストを修正するか、商品ボタンで直接入力してください。
            </div>
          )}
          <div className="row" style={{ marginTop: 8 }}>
            {result.paymentMethodCode && <span className="badge">支払方法: {result.paymentMethodCode}</span>}
            {result.discount > 0 && <span className="badge">値引き: ¥{result.discount.toLocaleString("ja-JP")}</span>}
            {result.guestCount && <span className="badge">人数: {result.guestCount}名</span>}
          </div>
        </div>
      )}

      <div className="row">
        <button className="btn" onClick={onCancel}>
          戻る
        </button>
        <button
          className="btn btn-primary btn-lg"
          disabled={!result || result.unmatchedItems.length > 0 || result.items.length === 0}
          onClick={handleApply}
        >
          カートに反映する
        </button>
      </div>
    </div>
  );
}
